<template>
  <div
    ref="table-container"
    class="relative flex-1 overflow-auto rounded-md border border-slate-300 dark:border-slate-300 dark:bg-slate-700 dark:text-gray-300"
  >
    <table
      v-if="items.length"
      class="dashboard-table third-party-cost-table"
      style="border-spacing: 0"
    >
      <thead>
        <draggable
          id="dragger"
          v-model="localColumns"
          item-key="id"
          tag="tr"
          :revert-on-spill="false"
          :draggable="'.draggable-cell'"
          :move="handleMove"
          @update="handleDrag"
        >
          <template #header>
            <th class="w-10 border-b border-gray-200/75 bg-white py-3.5 pl-3 pr-1 dark:bg-slate-700" />
          </template>
          <template #item="{ index }">
            <DashBoardHeadColumnComponent
              v-model:column="localColumns[index]"
              v-model:sort="localSort"
              v-model:cell-width="cellWidth"
              v-model:require-save-dashboard="localRequireSaveDashboard"
              :index="index"
              :is-last="columns.length === index + 1"
              :resize-able="columns.length > 1"
              :sum-able="true"
              :search-able="true"
              :available-column-fields="availableFields"
              :class="index === 0 ? 'sticky z-20' : ''"
              :style="index === 0 ? { left: '2.5rem' } : {}"
              @delete:column="handleDeleteColumns"
              @update:dashboard="updateDashboard"
            />
          </template>
        </draggable>
      </thead>
      <tbody>
        <ThirdPartyCostRowComponent
          v-for="(item, indexItem) in localItemsPaginated"
          :key="item.id"
          :item="item"
          :columns="columns"
          :cell-width="cellWidthComputed"
          :index-item="indexItem"
          :is-selected="selectedItems.includes(item.id)"
          @select="$emit('select-item', $event)"
          @print="$emit('print-item', $event)"
          @edit="$emit('edit-item', $event)"
        />
        <tr v-if="columns && countItems > page * pageSize">
          <td class="pagination" :colspan="columns.length + 1">
            <div
              class="flex flex-row gap-3"
              :style="{ width: `${visibleWidthTable}px` }"
            >
              <button
                class="bg-secondary text-contrast-secondary flex w-full cursor-pointer flex-row items-center justify-center gap-1 px-1 py-2 text-xs font-semibold"
                @click="page++"
              >
                <span>{{ $t('global.display_more') }}</span>
                <span>{{ page * pageSize }}/{{ countItems }}</span>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="hasSumAbleColumns" class="sticky bottom-0 z-30">
        <ThirdPartyCostRowComponent
          :item="itemsSums"
          :columns="sumColumns"
          :cell-width="cellWidthComputed"
          :index-item="1"
          :is-sum="true"
          :force-bold="true"
        />
      </tfoot>
    </table>
    <div
      v-else-if="loading"
      class="flex h-full w-full items-center justify-center"
    >
      <LoadingComponent size="small" />
    </div>
    <div v-else class="flex h-full w-full items-center justify-center">
      <h3
        class="border-primary bg-primary-100 rounded border px-2 py-0.5 text-base font-bold"
      >
        {{ $t('global.no_results') }}
      </h3>
    </div>
  </div>
</template>

<script>
import DashBoardHeadColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/DashBoardHeadColumnComponent.vue'
import ThirdPartyCostRowComponent from '@c/Dashboard/Content/ThirdPartyCost/components/Body/ThirdPartyCostRowComponent.vue'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'ThirdPartyCostTable',
  components: {
    LoadingComponent,
    ThirdPartyCostRowComponent,
    DashBoardHeadColumnComponent,
    draggable
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: false,
      default: () => ({})
    },
    requireSaveDashboard: {
      type: Boolean,
      required: false,
      default: false
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    selectedItems: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  emits: [
    'update:columns',
    'update:require-save-dashboard',
    'update:sort',
    'update:dashboard',
    'select-item',
    'print-item',
    'edit-item'
  ],
  data() {
    return {
      cellWidth: {},
      pageSize: 100,
      page: 1,
      localItems: [],
      visibleWidthTable: 0
    }
  },
  computed: {
    ...mapGetters({
      column_types: 'metadata/getColumnTypesThirdPartyCostDashboard'
    }),
    countItems() {
      return this.localItems.length
    },
    localItemsPaginated() {
      return this.localItems.slice(0, this.pageSize * this.page)
    },
    cellWidthComputed() {
      return Object.values(this.cellWidth).map((column) =>
        column == 'auto' ? 'auto' : `${Math.round(column)}px`
      )
    },
    availableFields() {
      return this.column_types || []
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    localColumns: {
      get() {
        return this.columns ?? []
      },
      set(value) {
        this.$emit('update:columns', value)
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    },
    hasSumAbleColumns() {
      return this.localColumns?.some((clm) => clm.sum_able) ?? false
    },
    sumColumns() {
      return this.localColumns.map((clm) => ({
        ...clm,
        type: clm.sum_able ? clm.type : 'text'
      }))
    },
    itemsSums() {
      const data = { id: 'total-sum' }
      this.localColumns.forEach((column, k) => {
        let sum = k < 1 ? this.$t('global.total') : null
        if (column.sum_able && k > 0) {
          sum = this.localItems
            .map((item) => item[column.field])
            .filter((count) => count?.toString()?.match(/[0-9.,]+/))
            .map((count) => parseFloat(count.toString().replace(',', '.')))
          if (sum.length) {
            sum = parseFloat(sum.reduce((a, b) => a + b, 0).toFixed(2))
          } else {
            sum = null
          }
        }
        data[column.field] = sum
      })
      return data
    }
  },
  watch: {
    items: {
      immediate: true,
      handler(newItems) {
        this.localItems = [...newItems]
        this.page = 1
      }
    },
    'columns.length': {
      immediate: true,
      handler() {
        const cellWidth = {}
        this.columns.forEach((column, index) => {
          cellWidth[index] = column.visual?.width || 'auto'
        })
        this.cellWidth = cellWidth
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.tableResized)
    this.tableResized()
  },
  unmounted() {
    window.removeEventListener('resize', this.tableResized)
  },
  methods: {
    tableResized() {
      this.visibleWidthTable = this.$refs['table-container']?.offsetWidth ?? 0
    },
    handleDrag() {
      if (!this.localRequireSaveDashboard) {
        this.localRequireSaveDashboard = true
      }
    },
    handleMove(e) {
      return e.related.previousSibling !== null
    },
    handleDeleteColumns(idx) {
      this.localColumns = this.localColumns.filter((_, i) => i !== idx)
      this.updateDashboard(false)
    },
    updateDashboard(value) {
      this.$emit('update:dashboard', value)
    }
  }
}
</script>
